import { t as tr } from "@/i18n";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Paintbrush, Trash2, Sparkles, Share2, Mail, Send, Loader2, BookOpen } from "lucide-react";
import { api, artworkImageUrl, downloadFile, formatApiError, pageImageUrl, slug } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Loader } from "@/components/Loader";
import { StickerShelf } from "@/components/StickerShelf";
import { ShareDialog, GalleryShareDialog } from "@/components/ShareDialog";
import { StoryDialog } from "@/components/StoryDialog";

const IconBtn = ({ onClick, icon: Icon, testid, title, danger, active }) => (
  <button onClick={onClick} data-testid={testid} title={title}
    className={`p-2.5 rounded-xl border-2 transition-colors ${danger ? "border-slate-200 bg-white hover:bg-red-50 hover:border-red-200 text-red-500" : active ? "border-[#4ECDC4] bg-[#4ECDC4]/20 text-[#1E293B]" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
    <Icon className="w-4 h-4" />
  </button>
);

const Empty = ({ text }) => (
  <div className="card-soft p-12 text-center space-y-4">
    <p className="font-display text-xl font-semibold">{text}</p>
    <Link to="/create" className="btn-pop btn-red inline-flex" data-testid="library-create-button"><Sparkles className="w-5 h-5" /> {tr("create_a_page")}</Link>
  </div>
);

const DigestBar = ({ user, setUser }) => {
  const [sending, setSending] = useState(false);
  const toggle = async (on) => {
    try {
      const { data } = await api.patch("/auth/me", { weekly_digest: on });
      setUser(data);
      toast.success(on ? "Weekly recap emails are on" : "Weekly recap emails are off");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const preview = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/digest/preview");
      if (data.sent) toast.success(`Recap sent to ${user.email} — ${data.pictures} picture${data.pictures === 1 ? "" : "s"}, ${data.stickers} sticker${data.stickers === 1 ? "" : "s"} this week.`);
      else toast.error("Couldn't send the email right now. Please try again later.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="card-soft px-5 py-4 flex flex-wrap items-center gap-4 mb-8" data-testid="digest-bar">
      <span className="w-10 h-10 rounded-2xl bg-[#FFD166] border-2 border-[#1E293B] flex items-center justify-center shrink-0"><Mail className="w-5 h-5" /></span>
      <div className="flex-1 min-w-[200px]">
        <p className="font-bold">{tr("weekly_recap_email")}</p>
        <p className="text-sm text-slate-600">Every Sunday: the pictures coloured and stickers earned this week, sent to {user.email}.</p>
      </div>
      <Switch checked={user.weekly_digest !== false} onCheckedChange={toggle} data-testid="digest-toggle" />
      <button onClick={preview} disabled={sending} className="btn-pop btn-white !py-2.5 !px-4 text-sm" data-testid="digest-preview-button">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send me this week's recap
      </button>
    </div>
  );
};

export default function Library() {
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const [pages, setPages] = useState(null);
  const [arts, setArts] = useState([]);
  const [sharing, setSharing] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [storyArt, setStoryArt] = useState(null);
  const firstName = (user?.name || "your child").split(" ").filter((p) => !/^(mr|mrs|ms|miss|mx|dr)\.?$/i.test(p))[0] || "your child";

  useEffect(() => {
    api.get("/pages").then((r) => setPages(r.data));
    api.get("/artworks").then((r) => setArts(r.data));
  }, []);

  const removePage = async (id) => {
    await api.delete(`/pages/${id}`);
    setPages((p) => p.filter((x) => x.id !== id));
    toast.success("Page removed");
  };
  const removeArt = async (id) => {
    await api.delete(`/artworks/${id}`);
    setArts((a) => a.filter((x) => x.id !== id));
    toast.success("Artwork removed");
  };

  const onShareChange = (updated) => {
    setArts((a) => a.map((x) => (x.id === updated.id ? updated : x)));
    setSharing(updated);
  };

  if (!pages) return <Loader label={tr("opening_your_library")} />;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="library-page">
      <ShareDialog art={sharing} onClose={() => setSharing(null)} onChange={onShareChange} />
      <StoryDialog art={storyArt} artist={firstName} onClose={() => setStoryArt(null)}
        onSaved={(id) => setArts((a) => a.map((x) => (x.id === id ? { ...x, has_story: true } : x)))} />
      <GalleryShareDialog open={galleryOpen} onClose={() => setGalleryOpen(false)} token={user?.gallery_token}
        onChange={(t) => setUser((u) => (u ? { ...u, gallery_token: t } : u))} />
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-8 rise">{tr("my_library")}</h1>
      <DigestBar user={user} setUser={setUser} />
      <Tabs defaultValue={params.get("tab") || "pages"}>
        <TabsList className="bg-[#F3EFE6] rounded-2xl p-1.5 h-auto mb-8">
          <TabsTrigger value="pages" data-testid="library-tab-pages" className="rounded-xl px-5 py-2.5 font-bold data-[state=active]:bg-[#FFD166]">Pages ({pages.length})</TabsTrigger>
          <TabsTrigger value="artworks" data-testid="library-tab-artworks" className="rounded-xl px-5 py-2.5 font-bold data-[state=active]:bg-[#4ECDC4]">Coloured ({arts.length})</TabsTrigger>
          <TabsTrigger value="stickers" data-testid="library-tab-stickers" className="rounded-xl px-5 py-2.5 font-bold data-[state=active]:bg-[#FF9FF3]">Stickers ({user?.stickers?.length || 0})</TabsTrigger>
        </TabsList>
        <TabsContent value="stickers"><StickerShelf earned={user?.stickers || []} badges={user?.badges || []} /></TabsContent>
        <TabsContent value="pages">
          {pages.length === 0 ? <Empty text={tr("no_pages_yet")} /> : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {pages.map((p) => (
                <div key={p.id} className="card-soft p-3 hover:border-sky-300" data-testid={`library-page-${p.id}`}>
                  <img src={pageImageUrl(p.id)} alt={p.title} loading="lazy" className="w-full aspect-square object-contain rounded-2xl bg-white" />
                  <div className="px-2 pt-3 space-y-3">
                    <div>
                      <p className="font-bold truncate">{p.title}</p>
                      <p className="text-xs text-slate-500">Ages {p.age_group} · {new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/colour/${p.id}`} className="flex-1 btn-pop btn-teal !py-2 !px-3 text-sm" data-testid={`library-colour-${p.id}`}><Paintbrush className="w-4 h-4" /> {tr("colour")}</Link>
                      <IconBtn icon={Download} title={tr("download_pdf")} testid={`library-pdf-${p.id}`} onClick={() => downloadFile(`/pages/${p.id}/pdf`, `${slug(p.title)}.pdf`)} />
                      <IconBtn icon={Trash2} danger title={tr("delete")} testid={`library-delete-${p.id}`} onClick={() => removePage(p.id)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="artworks">
          <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
            <p className="text-slate-600">{tr("every_picture_saved_from_colour_mode")}</p>
            <button onClick={() => setGalleryOpen(true)} data-testid="gallery-share-button"
              className={`btn-pop !py-2.5 !px-4 text-sm ${user?.gallery_token ? "btn-teal" : "btn-white"}`}>
              <Share2 className="w-4 h-4" /> {user?.gallery_token ? "Gallery link is on" : "Share my whole gallery"}
            </button>
          </div>
          {arts.length === 0 ? <Empty text={tr("nothing_coloured_yet_open_a_page_in_colo")} /> : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {arts.map((a) => (
                <div key={a.id} className="card-soft p-3" data-testid={`artwork-${a.id}`}>
                  <img src={artworkImageUrl(a.id)} alt={a.page_title} loading="lazy" className="w-full aspect-square object-contain rounded-2xl bg-white" />
                  <div className="flex items-center justify-between px-2 pt-3 gap-2">
                    <div className="min-w-0">
                      <p className="font-bold truncate">{a.page_title}</p>
                      <p className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <IconBtn icon={BookOpen} title={a.has_story ? "Story Time — story ready" : "Story Time"} testid={`artwork-story-${a.id}`} onClick={() => setStoryArt(a)} active={!!a.has_story} />
                      <IconBtn icon={Share2} title={a.share_token ? "Sharing on" : "Share"} testid={`artwork-share-${a.id}`} onClick={() => setSharing(a)} active={!!a.share_token} />
                      <IconBtn icon={Download} title={tr("download")} testid={`artwork-download-${a.id}`} onClick={() => downloadFile(`/artworks/${a.id}/image`, `${slug(a.page_title)}-coloured.png`)} />
                      <IconBtn icon={Trash2} danger title={tr("delete")} testid={`artwork-delete-${a.id}`} onClick={() => removeArt(a.id)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
